import React, { useMemo, useState, useCallback } from 'react';
import Link from '../../../../components/form/elements/link';
import {
  WAREHOUSE_DEAL_COPY,
  WAREHOUSE_DEAL_EDIT,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { can, route, routeQuery } from '../../../../services/utils';
import OptionModal from '../../../../components/modals/option-modal';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router';
import TabsKeys from '../../../../enums/tab-keys';
import {
  WAREHOUSE_MOVEMENT_INDEX,
  CROP_FIELDS_AND_WORKS_INDEX,
  CROP_YIELD_ACCOUNTING_CARGO_INDEX,
  LIVESTOCK_ANIMALS_AND_EVENTS_INDEX,
} from '../../../../router/routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { makeButton } from './form-parts';
import Deal from '../../../../interfaces/deal';
import RecycleDeal from '../../../../interfaces/recycle-deal';
import css from '../index.module.css';
import { Section } from '../../../../pages/crop/fields-and-works';
import AnimalsAndEventsTabsKeys from '../../../livestock/animals-and-events/enums/animals-and-events-tab-keys';
import { toggleAnimalCalendarOpen } from '../../../../store/global-slice';

const OUTGOING_DEAL_PRODUCT_IDS = 'outgoing_deal_product_ids';
const WORK_PRODUCT_WAREHOUSE = 'work_product_warehouse';
const CARGO_WAREHOUSE = 'cargo_warehouse';
const ANIMAL_EVENT = 'animal_event';

export default function useViewFooter(deal: Deal | RecycleDeal) {
  const history = useHistory();
  const { dealService } = useService();
  const { farmId, id, dealType } = useParams<{
    farmId: string;
    id: string;
    dealType: string;
  }>();
  const [modal, setModal] = useState(null);
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.warehouse.deals.'.concat(key)),
    [t]
  );
  const dispatch = useDispatch();
  const toggleCalendarOpen = (value: boolean) =>
    dispatch(toggleAnimalCalendarOpen(value));

  const modalYesButtonText = useCallback(() => {
    if (deal?.[OUTGOING_DEAL_PRODUCT_IDS].length) {
      return tp('move_to_deals');
    }

    if (deal?.[WORK_PRODUCT_WAREHOUSE]) {
      return tp('move_to_work');
    }

    if (deal?.[CARGO_WAREHOUSE]) {
      return tp('move_to_cargo');
    }

    if (deal?.[ANIMAL_EVENT]) {
      return tp('move_to_animal_event');
    }

    return t('yes');
  }, [deal, t, tp]);

  const modalContentText = useCallback(() => {
    if (deal?.[OUTGOING_DEAL_PRODUCT_IDS].length) {
      return (
        <div className={css['modal-delete_description']}>
          {tp('description_move_to_deals')}
        </div>
      );
    }

    if (deal?.[WORK_PRODUCT_WAREHOUSE]) {
      return (
        <div className={css['modal-delete_description']}>
          {tp('description_move_to_work')}
        </div>
      );
    }

    if (deal?.[CARGO_WAREHOUSE]) {
      return (
        <div className={css['modal-delete_description']}>
          {tp('description_move_to_cargo')}
        </div>
      );
    }

    if (deal?.[ANIMAL_EVENT]) {
      return (
        <div className={css['modal-delete_description']}>
          {tp('description_move_to_animal_event')}
        </div>
      );
    }

    return;
  }, [deal, tp]);

  const modalTitleText = useCallback(() => {
    if (deal?.[OUTGOING_DEAL_PRODUCT_IDS].length) {
      return tp('relationship_deals_title');
    }

    if (deal?.[WORK_PRODUCT_WAREHOUSE]) {
      return tp('relationship_work_title');
    }

    if (deal?.[CARGO_WAREHOUSE]) {
      return tp('relationship_cargo_title');
    }

    if (deal?.[ANIMAL_EVENT]) {
      return tp('relationship_animal_event_title');
    }

    return tp('delete_title');
  }, [deal, tp]);

  const deleteWorkModal = useCallback(() => {
    return (
      <OptionModal
        title={modalTitleText()}
        className={css['delete-modal_wrapper']}
        content={modalContentText()}
        buttons={[
          {
            title: modalYesButtonText(),
            name: 'yes',
            value: true,
            ignoreValidation: true,
          },
          {
            title:
              deal?.[OUTGOING_DEAL_PRODUCT_IDS].length ||
              deal?.[WORK_PRODUCT_WAREHOUSE] ||
              deal?.[CARGO_WAREHOUSE] ||
              deal?.[ANIMAL_EVENT]
                ? t('cancel')
                : t('no'),
            name: 'no',
            value: false,
            ignoreValidation: true,
          },
        ]}
        onChoice={(choice) => {
          if (choice) {
            if (deal?.[OUTGOING_DEAL_PRODUCT_IDS].length) {
              sessionStorage.removeItem('current_index_page');
              history.push(
                routeQuery(
                  route(WAREHOUSE_MOVEMENT_INDEX, {
                    farmId: farmId,
                    tab: TabsKeys.deals,
                  }),
                  {
                    ids: deal?.[OUTGOING_DEAL_PRODUCT_IDS],
                  }
                )
              );
            } else if (deal?.[WORK_PRODUCT_WAREHOUSE]) {
              const work = deal?.[WORK_PRODUCT_WAREHOUSE].work;
              const field_lad_number = work?.map((work) => work.id);

              history.push(
                routeQuery(
                  route(CROP_FIELDS_AND_WORKS_INDEX, {
                    farmId: farmId,
                    season:
                      work?.[0]?.season_id ?? new Date(deal.date).getFullYear(),
                    tab: Section.workOverview,
                  }),
                  {
                    done: true,
                    ...(deal?.[WORK_PRODUCT_WAREHOUSE]?.type && {
                      type: deal?.[WORK_PRODUCT_WAREHOUSE]?.type.id,
                    }),
                    ...(field_lad_number &&
                      field_lad_number.length > 0 && { field_lad_number }),
                  }
                )
              );
            } else if (deal?.[CARGO_WAREHOUSE]) {
              history.push(
                route(CROP_YIELD_ACCOUNTING_CARGO_INDEX, {
                  farmId: farmId,
                  season: new Date(deal.date).getFullYear(),
                  yield_id: `${deal?.[CARGO_WAREHOUSE].workField.work_id}|${deal?.[CARGO_WAREHOUSE].workField.field_id}`,
                })
              );
            } else if (deal?.[ANIMAL_EVENT]) {
              toggleCalendarOpen(false);
              history.push(
                routeQuery(
                  route(LIVESTOCK_ANIMALS_AND_EVENTS_INDEX, {
                    farmId: farmId,
                    tab: AnimalsAndEventsTabsKeys.events,
                  }),
                  {
                    from: deal?.[ANIMAL_EVENT]?.date
                      ? `${deal?.[ANIMAL_EVENT]?.date.split('-')[0]}-01-01`
                      : `${
                          deal?.[ANIMAL_EVENT]?.date_from.split('-')[0]
                        }-01-01`,
                    to: new Date().toISOString().split('T')[0],
                    species: deal?.[ANIMAL_EVENT].animals[0].animal_species_id,
                    sub_event: deal?.[ANIMAL_EVENT].sub_type ? `${deal?.[ANIMAL_EVENT].type}_${deal?.[ANIMAL_EVENT].sub_type}` : deal?.[ANIMAL_EVENT].type,
                    animal_eid: deal?.[ANIMAL_EVENT].animals[0].animal_id,
                  }
                )
              );
            } else {
              dealService.destroy(farmId, id).subscribe(() => {
                history.push(
                  route(WAREHOUSE_MOVEMENT_INDEX, {
                    farmId,
                    tab: TabsKeys.deals,
                  })
                );
              });
            }
          }

          setModal(null);
        }}
        onClose={() => setModal(null)}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    t,
    dealService,
    farmId,
    id,
    history,
    deal,
    modalYesButtonText,
    modalContentText,
    modalTitleText,
  ]);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const onDelete = useCallback(() => {
    setModal(deleteWorkModal);
  }, [deleteWorkModal]);

  const footer = useMemo(() => {
    let elements = [];

    if (can(farmUser, { title: 'update deal' })) {
      elements.push(
        Link(
          route(WAREHOUSE_DEAL_EDIT, { farmId, id, dealType }),
          'edit',
          true
        ),
        makeButton('delete', '', '', null, false, onDelete)
      );
    }

    if (modal) {
      elements.push(() => modal);
    }

    if (can(farmUser, { title: 'create deal' })) {
      elements.push(
        Link(
          route(WAREHOUSE_DEAL_COPY, { farmId, id, dealType }),
          'copy',
          false
        )
      );
    }

    return elements;
  }, [modal, farmUser, farmId, id, dealType, onDelete]);

  return footer;
}
